/** @jsx jsx */
import { Helmet } from 'react-helmet'
import Breadcrumbs from '../../componentsV2/breadcrumbs'
import useBreadcrumbs from '../../componentsV2/hooks/useBreadcrumbs'
import { FeaturedCollectionInterface } from '../collections-header/types'
import { PageProps } from 'gatsby'
import { ReactNode, useEffect, useMemo, useRef } from 'react'
import { jsx, Container, ThemeUICSSObject, Divider, Flex } from 'theme-ui'
import { FeaturedCollectionType, HeaderVariantType, HeroHeader, Maybe } from '~/@types/models'
import CollectionsHeader from '~/components/collections-header'
import Layout from '~/components/layout/index'
import useShopifyBreadcrumbs from '~/componentsV2/hooks/useShopifyBreadcrumbs'
import useMediaQuery from '~/hooks/use-media-query'
import FilterProvider from '~/context/filters'
import InstantSearchWrapper from '~/features/instant-search'
import { useCustomerContext } from '~/context/customer-context'
import { BespokeInnerMenu } from '~/containers/product-detail-v2/bespoke-detail/bespoke-inner-menu'

type TCatalog = {
  retail: string
  trade: string
  all: string
}

interface Props extends Partial<Omit<PageProps, 'children'>> {
  title: string
  seoTitle?: string
  seoDescription?: string
  heading?: string
  handle: string
  description?: string
  image?: Maybe<string>
  contentAfter?: ReactNode
  headerVariant?: HeaderVariantType
  heroHeader?: Maybe<HeroHeader>
  containerSx?: ThemeUICSSObject
  showFeaturedCollections?: boolean
  featuredCollectionsList?: FeaturedCollectionType[]
  ankleContent?: string
  noIndex?: boolean
  children: ReactNode
  catalog: TCatalog
  headerDescription?: string
}

const Catalog = (props: TCatalog) => {
  const { retail, trade, all } = props
  const { isTrade } = useCustomerContext()

  const url = useMemo(() => {
    const defaultRetailUrl = isTrade && trade ? trade : retail || all
    return defaultRetailUrl
  }, [isTrade, retail, trade])
  const ref = useRef<HTMLIFrameElement>(null)

  useEffect(() => {
    if (!url) {
      return
    }
    const iframe = ref.current
    const handleLoad = () => {
      if (iframe?.contentDocument) {
        const styleSheet = document.createElement('style')
        styleSheet.id = 'custom-bsol'
        styleSheet.innerHTML = `
          div[class*="WatermarkBarStyledComponentsstyles"] {
            display: none !important;
          }
          a[class*="WatermarkBarStyledComponentsstyles"] {
            display: none !important;
          }
        `
        iframe.contentDocument.head.appendChild(styleSheet)
      }
    }

    iframe?.addEventListener('load', handleLoad)

    return () => {
      iframe?.removeEventListener('load', handleLoad)
    }
  }, [url, ref.current])

  if (!url) {
    return null
  }

  return (
    <Flex
      sx={{
        justifyContent: `center`,
      }}
    >
      <iframe
        src={url}
        ref={ref}
        seamless="seamless"
        scrolling="no"
        frameBorder="0"
        allowFullScreen
        allow="autoplay; clipboard-read; clipboard-write"
        sx={{
          width: `100%`,
          aspectRatio: [1, null, null, `16/9`],
        }}
      ></iframe>
      <Divider />
    </Flex>
  )
}

const CollectionLayout = ({
  title,
  seoTitle,
  seoDescription,
  heading,
  handle,
  description,
  image,
  children,
  contentAfter,
  headerVariant,
  heroHeader = null,
  containerSx,
  featuredCollectionsList,
  ankleContent,
  noIndex,
  catalog,
  headerDescription,
  templateKey,
  ...rest
}: Props) => {
  const isMobile = useMediaQuery({
    query: breakpoints => `(max-width: ${breakpoints[2]})`,
  })

  const { breadcrumbs } = useShopifyBreadcrumbs()

  const showFeaturedCollections = !!featuredCollectionsList?.length

  return (
    <Layout
      title={title}
      seoTitle={seoTitle || title}
      seoDescription={seoDescription || description}
      description={description}
      ankleContent={ankleContent}
      image={image as string}
      headerVariant={headerVariant ? 'light' : 'default'}
      isCollection
      template={templateKey}
      {...rest}
    >
      <Helmet>
        <meta name="robots" content={noIndex ? 'noindex' : 'index'} />
      </Helmet>

      {templateKey === 'bespoke' && <BespokeInnerMenu />}
      <CollectionsHeader
        title={heading || title}
        description={description}
        heroHeader={heroHeader}
        headerDescription={headerDescription}
        // containerSx={containerSx}
        showFeaturedCollections={showFeaturedCollections}
        handle={handle}
        featuredCollectionsList={featuredCollectionsList}
      />
      <InstantSearchWrapper>
        <FilterProvider>
          <Container
            className="collection-container"
            my={['md']}
            px={['md']}
            style={{ position: 'relative' }}
          >
            {!isMobile && <Breadcrumbs breadcrumbs={breadcrumbs} />}
            <Divider />
            <Catalog {...catalog} />

            {children}
          </Container>
        </FilterProvider>
      </InstantSearchWrapper>
      {contentAfter}
    </Layout>
  )
}

export default CollectionLayout
